/**
 * Mirror the app-wide unread count onto the desktop taskbar when running inside
 * the Electron shell. The renderer draws a tiny badge image and hands it to the
 * main process (which can't run a canvas) via the preload bridge; main puts it
 * on the Windows taskbar icon (or a macOS/Linux dock badge). No-op in a plain
 * browser, and on older shells that lack the `setUnread` bridge.
 */
import { useDataStore } from '@/stores/dataStore';
import { readUnreadTotals } from './unreadTotals';

interface DesktopBridge {
  isDesktop?: boolean;
  setUnread?: (p: { count: number; mentions: number; dataUrl: string | null }) => void;
}

/** A standalone overlay badge (transparent, ~32px): red with a count for
 *  mentions, accent with the unread count otherwise. */
function badgeDataUrl(total: number, mentions: number): string | null {
  if (total <= 0 && mentions <= 0) return null;
  const S = 32;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const n = mentions > 0 ? mentions : total;
  ctx.beginPath();
  ctx.arc(S / 2, S / 2, S / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle = mentions > 0 ? '#f23f43' : '#0485f7';
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${n > 9 ? 16 : 20}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(n > 9 ? '9+' : String(n), S / 2, S / 2 + 1);
  return canvas.toDataURL('image/png');
}

export function installDesktopBadge(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const bridge = (window as unknown as { bunkerDesktop?: DesktopBridge }).bunkerDesktop;
  if (!bridge?.isDesktop || typeof bridge.setUnread !== 'function') return () => undefined;
  const send = bridge.setUnread.bind(bridge);

  let lastKey = ' ';
  const apply = () => {
    const { total, mentions } = readUnreadTotals();
    // Count matters here (the overlay shows a number), so encode it in the key.
    const key = mentions > 0 ? `m${Math.min(mentions, 10)}` : total > 0 ? `u${Math.min(total, 10)}` : '';
    if (key === lastKey) return;
    lastKey = key;
    send({ count: total, mentions, dataUrl: badgeDataUrl(total, mentions) });
  };
  apply();
  return useDataStore.subscribe(apply);
}
